import { cn } from '@/lib/utils';
import { Chip } from './Chip';
import { PAPER_INK, PAPER_RULE } from './paper-tokens';

export type IllustrativeLabelProps = {
  text?: string;
  className?: string;
  /** R4-P4 D4 fix (additive, P2.7-pattern unfreeze — masterplan §1.3 sanctions
   * this exact exception): the inner `Chip variant="system"` hardcodes
   * `text-white/60` + `border-[#FFFFFF33]`, tuned for the dark `ui/**`
   * bands. `BigStatCurve` (the light editorial band) composes this label
   * with no way to make it legible, rendering white/60-on-`#F6F5F2` —
   * effectively invisible on the ONE component whose whole job is honesty.
   * `surface='light'` overrides Chip's border/text to the audited light-band
   * ink tokens (masterplan [AMENDED per C1]: `#5C616B` measures 5.70:1 on
   * `#F6F5F2`, WCAG AA).
   *
   * 2026-07-18 (live-drive finding, slop-audit follow-up): `surface='paper'`
   * added — a THIRD, R7-warm-family variant. `HeroChatReveal.tsx` composes
   * this label with NO surface prop (defaulting to `'dark'`), statically
   * positioned below the phone exhibit — but that position sits in
   * `SolutionSection.tsx`'s paper zone, OUTSIDE the phone's own dark bezel
   * (the exhibit interior itself stays dark and legal; only the caption
   * beneath it is page chrome). `'dark'`'s hardcoded `text-white/60` there
   * measured near-invisible on paper. `'paper'` overrides to `PAPER_INK.
   * muted` + `PAPER_RULE.hairline`, the warm R7 family — `BigStatCurve.tsx`
   * (previously `surface='light'`, the OLDER cooler `ui-light` family, kept
   * as a stated compromise during the earlier integration pass) also moves
   * to `'paper'` in the same edit, closing that documented residue.
   * Defaults to `'dark'` — byte-identical to every existing call site that
   * doesn't opt in; `Chip`'s own `className` merge (tailwind-merge) lets
   * this override the variant's colors without touching Chip.tsx. */
  surface?: 'dark' | 'light' | 'paper';
};

const DEFAULT_TEXT = 'Interfață ilustrativă';

/**
 * IllustrativeLabel — the honest "this is a mock" marker every filled-mock
 * figure carries (R4-P2 build brief §2 + §6.2 "illustrative-label law made
 * structural"). Default anatomy: a system `Chip` pinned to a figure's
 * bottom-right corner. A host primitive with its own documented label
 * placement (e.g. `MockChart`'s header slot, dossier §2.5) overrides the
 * position via `className` — `absolute`/`bottom-*`/`right-*` are a normal
 * Tailwind utility group, so `className="static"` cleanly replaces them
 * (`cn` runs `tailwind-merge`).
 *
 * No disabling boolean exists on purpose (§6.2): every mass-bearing
 * composite renders exactly one of these, unconditionally.
 */
export function IllustrativeLabel({
  text = DEFAULT_TEXT,
  className,
  surface = 'dark',
}: IllustrativeLabelProps) {
  return (
    <span
      data-primitive="IllustrativeLabel"
      data-surface={surface}
      className={cn('pointer-events-none absolute bottom-2 right-2 z-10', className)}
    >
      <Chip
        variant="system"
        label={text}
        className={cn(
          surface === 'light' && 'border-[rgba(0,0,0,0.12)] text-[#5C616B]',
          surface === 'paper' && cn(PAPER_RULE.hairline.border, PAPER_INK.muted.text)
        )}
      />
    </span>
  );
}
